import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Tag, CaretDown, Check } from '@phosphor-icons/react';
import { useMBList } from '../hooks/useMBList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact "Assign MB" quick-picker shown in chat headers. Renders as an amber
 * pill when the customer has NO MB yet — perfect for the N (New Members) flow
 * so admin/handler can tag them without leaving the chat.
 *
 * Props:
 *   customerId   – required. Customer's user id.
 *   currentMB    – current mb_setup value (string or null/'')
 *   actorId      – logged-in staff user id
 *   actorRole    – logged-in staff role
 *   onAssigned   – (mb) => void  callback after successful assign
 *   compact      – boolean; when true renders as a small pill instead of button
 */
const AssignMBQuickPicker = ({ customerId, currentMB, actorId, actorRole, onAssigned, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // {top,left} fixed position (Portal)
  const ref = useRef(null);
  const btnRef = useRef(null);
  const MB_OPTIONS = useMBList();

  // Point 10a — the dropdown used to render `absolute` inside scrollable N
  // containers, so it got clipped and the user had to scroll to see the MB
  // options. Render it in a Portal with FIXED position anchored to the button
  // so it's always immediately visible on click (no scrolling needed).
  const computePos = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuW = 160;
    const left = Math.max(8, Math.min(r.right - menuW, window.innerWidth - menuW - 8));
    setMenuPos({ top: r.bottom + 6, left });
  };

  useEffect(() => {
    if (!open) return;
    computePos();
    const onClick = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-mb-portal-menu="1"]')) return;
      setOpen(false);
    };
    const onReposition = () => computePos();
    document.addEventListener('click', onClick);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  const assign = async (mb) => {
    if (!customerId) { toast.error('Missing customer'); return; }
    const previousMB = currentMB || null;
    setBusy(true);
    try {
      await axios.patch(`${API}/clients/${customerId}/mb-setup`, {
        mb_setup: mb, actor_id: actorId,
      });
      setOpen(false);
      onAssigned && onAssigned(mb);
      // 5-second Undo — revert to previousMB on click.
      const undoAssign = async () => {
        try {
          await axios.patch(`${API}/clients/${customerId}/mb-setup`, {
            mb_setup: previousMB, actor_id: actorId,
          });
          onAssigned && onAssigned(previousMB);
          toast.success(previousMB ? `Restored to ${previousMB}` : 'MB assignment undone');
        } catch (e) {
          toast.error('Undo failed');
        }
      };
      toast.success(mb ? `Assigned to ${mb}` : 'MB cleared', {
        duration: 5000,
        action: { label: 'Undo', onClick: undoAssign },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    } finally { setBusy(false); }
  };

  const hasMB = !!(currentMB && String(currentMB).trim());
  // Handlers can only re-assign for customers who have no MB yet; super_admin
  // can always change. But this component is primarily shown in the "unassigned"
  // state — parent decides when to render it.
  if (!actorId) return null;

  const pillClass = hasMB
    ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
    : 'bg-amber-500 text-white border-amber-600 animate-pulse hover:animate-none';

  return (
    <div className="relative" ref={ref} data-testid="assign-mb-quick-picker">
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        disabled={busy}
        className={`inline-flex items-center gap-1 ${compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]'} rounded-full border font-bold shadow-sm transition-all ${pillClass} disabled:opacity-60`}
        data-testid="assign-mb-toggle-btn"
        title={hasMB ? 'Change MB' : 'Assign an MB — this customer has none yet'}
      >
        <Tag size={compact ? 10 : 12} weight="bold" />
        <span>{hasMB ? currentMB : 'Assign MB'}</span>
        <CaretDown size={compact ? 8 : 10} weight="bold" />
      </button>
      {open && menuPos && createPortal(
        <div
          data-mb-portal-menu="1"
          className="fixed z-[9999] bg-white text-slate-800 rounded-xl shadow-2xl border border-slate-200 w-[160px] py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
          data-testid="assign-mb-menu"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 border-b border-slate-100 mb-0.5">
            Pick an MB
          </div>
          {MB_OPTIONS.map((mb) => {
            const selected = hasMB && currentMB === mb;
            return (
              <button
                key={mb}
                onClick={() => assign(mb)}
                disabled={busy || selected}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-[13px] hover:bg-amber-50 disabled:opacity-50 ${selected ? 'text-indigo-700 font-bold' : 'text-slate-800'}`}
                data-testid={`assign-mb-opt-${mb}`}
              >
                <span>{mb}</span>
                {selected && <Check size={14} weight="bold" className="text-indigo-600" />}
              </button>
            );
          })}
          {hasMB && (
            <>
              <div className="my-0.5 border-t border-slate-100" />
              <button
                onClick={() => assign(null)}
                disabled={busy}
                className="w-full text-left px-3 py-1.5 text-[12px] text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                data-testid="assign-mb-clear"
              >
                Clear MB
              </button>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default AssignMBQuickPicker;
